import React, { useState, useEffect } from 'react';
import { api } from '../api';
import ThinkingIndicator from './ThinkingIndicator';
import PreviewFrame from './PreviewFrame';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { useLocale } from '../i18n/LocaleContext';

// Artifact delivery: download through the existing authenticated API client, never through a bare browser GET.

const PROJECT_TYPES = [
  { value: 'react-app', labelKey: 'builder.reactApp', hintKey: 'builder.reactAppHint' },
  { value: 'next-app', labelKey: 'builder.nextApp', hintKey: 'builder.nextAppHint' },
  { value: 'node-api', labelKey: 'builder.nodeApi', hintKey: 'builder.nodeApiHint' },
  { value: 'python-flask', labelKey: 'builder.flaskApp', hintKey: 'builder.flaskAppHint' },
  { value: 'python-fastapi', labelKey: 'builder.fastApi', hintKey: 'builder.fastApiHint' },
  { value: 'static-html', labelKey: 'builder.staticSite', hintKey: 'builder.staticSiteHint' }
];
const TARGET_OPTIONS = [
  { value: 'WEB', labelKey: 'builder.web', hintKey: 'builder.webHint' },
  { value: 'BACKEND', labelKey: 'builder.backend', hintKey: 'builder.backendHint' },
  { value: 'FULL_STACK', labelKey: 'builder.fullStack', hintKey: 'builder.fullStackHint' },
  { value: 'MOBILE', labelKey: 'builder.mobile', hintKey: 'builder.mobileHint' },
  { value: 'DESKTOP', labelKey: 'builder.desktop', hintKey: 'builder.desktopHint' }
];
const PLATFORM_OPTIONS = Object.freeze({
  MOBILE: ['ANDROID', 'IOS'],
  DESKTOP: ['WINDOWS', 'MACOS', 'LINUX']
});
const ARTIFACT_ONLY_TARGETS = new Set(['BACKEND', 'FULL_STACK']);

function ProjectBuilder({ onBuildStarted, onNavigateToAccount }) {
  // Target status clarity: the Builder only exposes executable paths and distinguishes the
  // MOBILE browser companion from the Android-only verified package contract and from native targets that still lack delivery evidence.
  const { t } = useLocale();
  const [form, setForm] = useState({ name: '', target: 'WEB', targetPlatforms: [], type: '', description: '', github: false, githubRepository: { name: '', private: true }, deploy: false, domain: '' });
  const [status, setStatus] = useState(null);
  const [activity, setActivity] = useState(''); // Client Activity Stream (UX only) - friendly copy of what the agent is doing right now; the Requirements Quality Gate on the server, not this, decides pass/fail
  const [error, setError] = useState('');
  // Plain-language "what is this?" toggle for the Target/Project-type option grids (2026-09-03):
  // a non-technical customer can't tell what "Backend / API" or "FastAPI" actually means or
  // what they'd be able to build with it - this lets them tap an info icon on ANY option (not
  // just the one they've already picked) and get a short explanation in plain words, without
  // leaving the Builder screen or reading documentation. { group: 'target'|'type', value } | null.
  const [expandedHint, setExpandedHint] = useState(null);
  const toggleHint = (group, value) => setExpandedHint(current => (current && current.group === group && current.value === value) ? null : { group, value });
  // Terminal "a customer decision is needed" state (2026-08-20): set only when auto-repair is
  // exhausted for a specific requirement (or that requirement was unverifiable from the
  // start) - see task.awaitingCustomerDecision/task.customerDecision in orchestrator.js. Kept
  // separate from `error` so its own approve action can be shown instead of the generic banner.
  const [decision, setDecision] = useState(null);
  const [resolvingDecision, setResolvingDecision] = useState(false);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);

  const handleApiError = (err) => {
    setError(err.response?.data?.error || err.message);
    setNeedsUpgrade(Boolean(err.response?.data?.insufficientCredits || err.response?.data?.killSwitchTripped));
  };
  const [result, setResult] = useState(null);
  const [downloadingArtifact, setDownloadingArtifact] = useState(false);
  const [confirmingDeploy, setConfirmingDeploy] = useState(false);
  const [deploymentApproval, setDeploymentApproval] = useState(null);
  const [uploadDeploymentApproval, setUploadDeploymentApproval] = useState(null);
  const [approvingDeployment, setApprovingDeployment] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [resultPreviewOpen, setResultPreviewOpen] = useState(false);
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [neededEnvVars, setNeededEnvVars] = useState(null); // structured [{name, comment, provider, providerUrl}]
  const [envValues, setEnvValues] = useState({});
  const [consentRequired, setConsentRequired] = useState(false);
  const [ownershipConsent, setOwnershipConsent] = useState(false);
  const [github, setGithub] = useState({ connected: false, username: null, loading: true });
  const [cloudflare, setCloudflare] = useState({ connected: false, accountName: null, loading: true });
  const [netlify, setNetlify] = useState({ connected: false, loading: true });
  const [firebase, setFirebase] = useState({ connected: false, loading: true });
  const [firebaseProjects, setFirebaseProjects] = useState({ projects: [], selected: null, loading: true });
  const [vercel, setVercel] = useState({ connected: false, loading: true });
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('cloudflare-pages');
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [planPreview, setPlanPreview] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planApproved, setPlanApproved] = useState(false);
  const [repositoryAvailability, setRepositoryAvailability] = useState({ checking: false, available: null, name: '', reason: '' });
  const [existingRepository, setExistingRepository] = useState({ open: false, loading: false, repositories: [], fullName: '', branches: [], branch: '', inspecting: false, inspection: null });

  const renewBuildPreview = async (isUpload = false) => {
    const current = isUpload ? uploadResult : result;
    if (!current?.sandboxJobId) throw new Error('This preview cannot be renewed because no sandbox job is available.');
    const renewed = await api.createSandboxPreviewSession(current.sandboxJobId);
    const setCurrent = isUpload ? setUploadResult : setResult;
    setCurrent(previous => ({ ...previous, previewUrl: renewed.previewUrl }));
    return renewed.previewUrl;
  };

  const downloadVerifiedArtifact = async () => {
    if (!result?.sandboxJobId) return;
    setDownloadingArtifact(true);
    try {
      const response = await api.downloadVerifiedArtifact(result.sandboxJobId);
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const filename = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1] || 'codevoyage-verified-artifact.tar.gz';
      const objectUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (err) {
      handleApiError(err);
    } finally {
      setDownloadingArtifact(false);
    }
  };

  useEffect(() => {
    // A changed scope, ownership target or deployment destination invalidates a prior approval.
    setPlanApproved(false);
  }, [form.name, form.target, form.targetPlatforms, form.type, form.description, form.github, form.githubRepository.name, form.githubRepository.private, form.deploy, selectedProvider]);

  useEffect(() => {
    if (!form.github || !github.connected) {
      setRepositoryAvailability({ checking: false, available: null, name: '', reason: '' });
      return undefined;
    }
    const name = form.githubRepository.name.trim();
    if (!/^[A-Za-z0-9][A-Za-z0-9_.-]{0,99}$/.test(name)) {
      setRepositoryAvailability({ checking: false, available: null, name, reason: name ? 'Use letters, numbers, dots, hyphens or underscores.' : '' });
      return undefined;
    }
    let cancelled = false;
    setRepositoryAvailability({ checking: true, available: null, name, reason: '' });
    const timer = window.setTimeout(() => {
      api.githubRepositoryAvailability(name)
        .then(response => {
          if (!cancelled) setRepositoryAvailability({ checking: false, ...response.availability });
        })
        .catch(err => {
          if (!cancelled) setRepositoryAvailability({ checking: false, available: null, name, reason: err.response?.data?.error || 'Unable to check this repository name right now.' });
        });
    }, 450);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [form.github, form.githubRepository.name, github.connected]);

  useEffect(() => {
    api.githubStatus().then(res => setGithub({ connected: res.connected, username: res.username, loading: false })).catch(() => setGithub(g => ({ ...g, loading: false })));
    api.cloudflareStatus().then(res => setCloudflare({ connected: res.connected, accountName: res.accountName, loading: false })).catch(() => setCloudflare(c => ({ ...c, loading: false })));
    api.netlifyStatus().then(res => setNetlify({ connected: res.connected, loading: false })).catch(() => setNetlify(n => ({ ...n, loading: false })));
    api.firebaseStatus().then(res => setFirebase({ connected: res.connected, loading: false })).catch(() => setFirebase(f => ({ ...f, loading: false })));
    api.firebaseProjects().then(res => setFirebaseProjects({ projects: res.projects || [], selected: res.selected || null, loading: false })).catch(() => setFirebaseProjects(f => ({ ...f, loading: false })));
    api.vercelStatus().then(res => setVercel({ connected: res.connected, loading: false })).catch(() => setVercel(v => ({ ...v, loading: false })));
    api.deployProviders().then(res => setProviders(res.providers)).catch(() => {});
  }, []);

  // Single source of truth for provider connection state and connect actions - a map here
  // means adding a future provider only needs one new entry, not another branch threaded
  // through multiple functions (the previous nested-ternary version is exactly how
  // Firebase ended up silently missing from both connectedForProvider and
  // handleConnectProvider for a while).
  const PROVIDER_CONNECTIONS = {
    'cloudflare-pages': { state: cloudflare, connect: api.cloudflareConnect },
    'netlify': { state: netlify, connect: api.netlifyConnect },
    'firebase-hosting': { state: firebase, connect: api.firebaseConnect },
    'vercel': { state: vercel, connect: api.vercelConnect }
  };

  const connectedForProvider = (id) => PROVIDER_CONNECTIONS[id]?.state.connected || false;
  const providerLoading = (id) => PROVIDER_CONNECTIONS[id]?.state.loading ?? false;

  const handleConnectProvider = async (id) => {
    try {
      const connectFn = PROVIDER_CONNECTIONS[id]?.connect;
      if (!connectFn) throw new Error(`No connect action available for "${id}"`);
      const res = await connectFn();
      window.location.href = res.authorizeUrl;
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleConnectGithub = async () => {
    try {
      const res = await api.githubConnect();
      window.location.href = res.authorizeUrl;
    } catch (err) {
      handleApiError(err);
    }
  };

  const loadExistingRepositories = async () => {
    setExistingRepository(current => ({ ...current, open: true, loading: true, inspection: null }));
    try {
      const response = await api.githubRepositories();
      setExistingRepository(current => ({ ...current, loading: false, repositories: response.repositories || [] }));
    } catch (err) {
      setExistingRepository(current => ({ ...current, loading: false }));
      handleApiError(err);
    }
  };

  const selectExistingRepository = async (fullName) => {
    setExistingRepository(current => ({ ...current, fullName, branches: [], branch: '', loading: Boolean(fullName), inspection: null }));
    if (!fullName) return;
    try {
      const response = await api.githubRepositoryBranches(fullName);
      setExistingRepository(current => ({ ...current, loading: false, branches: response.branches || [], branch: response.branches?.[0]?.name || '' }));
    } catch (err) {
      setExistingRepository(current => ({ ...current, loading: false }));
      handleApiError(err);
    }
  };

  const inspectExistingRepository = async () => {
    if (!existingRepository.fullName || !existingRepository.branch) return;
    setExistingRepository(current => ({ ...current, inspecting: true, inspection: null }));
    setError('');
    try {
      const response = await api.inspectGithubRepository(existingRepository.fullName, existingRepository.branch);
      setExistingRepository(current => ({ ...current, inspecting: false, inspection: response.inspection }));
    } catch (err) {
      setExistingRepository(current => ({ ...current, inspecting: false }));
      handleApiError(err);
    }
  };

  const handleFirebaseProjectChange = async (projectId) => {
    const previousProjectId = firebaseProjects.selected;
    setFirebaseProjects(current => ({ ...current, selected: projectId }));
    try {
      await api.selectFirebaseProject(projectId);
    } catch (err) {
      setFirebaseProjects(current => ({ ...current, selected: previousProjectId }));
      handleApiError(err);
    }
  };

  const getDeployOptions = ({ continuation = false } = {}) => ({
    github: continuation || form.github,
    deploy: continuation ? false : !ARTIFACT_ONLY_TARGETS.has(form.target),
    deployProvider: selectedProvider,
    domain: form.domain.trim() || undefined,
    envVars: Object.keys(envValues).length > 0 ? envValues : undefined,
    ownershipConsent: ownershipConsent || undefined
  });

  const requestPlan = async () => {
    const githubSource = existingRepository.inspection ? { inspectionId: existingRepository.inspection.id } : undefined;
    if (githubSource && form.github) return setError('Choose either a new repository or the inspected existing repository, not both.');
    if (!form.name.trim()) return setError('Give the project a name before reviewing its build plan.');
    if (!form.type && !form.description.trim()) return setError('Choose a project type or describe what you want to build before reviewing its plan.');
    if (form.github && !github.connected) return setError('Connect your own GitHub account before selecting a repository.');
    if (form.github && repositoryAvailability.checking) return setError('Wait for the GitHub repository name check to finish.');
    if (form.github && repositoryAvailability.available !== true) return setError(repositoryAvailability.reason || 'Choose an available GitHub repository name before reviewing the plan.');
    setError('');
    setPlanLoading(true);
    try {
      const response = await api.buildPlan({
        projectType: form.type || undefined,
        target: form.target,
        targetPlatforms: form.targetPlatforms,
        name: form.name,
        description: form.description || undefined,
        github: githubSource ? false : form.github,
        githubRepository: form.github ? form.githubRepository : undefined,
        githubSource,
        deploy: githubSource || ARTIFACT_ONLY_TARGETS.has(form.target) ? false : form.deploy,
        deployProvider: !githubSource && !ARTIFACT_ONLY_TARGETS.has(form.target) && form.deploy ? selectedProvider : undefined
      });
      setPlanPreview(response.plan);
      setPlanApproved(false);
    } catch (err) {
      handleApiError(err);
    } finally {
      setPlanLoading(false);
    }
  };

  const startBuild = async () => {
    const githubSource = existingRepository.inspection ? { inspectionId: existingRepository.inspection.id } : undefined;
    setError('');
    setDecision(null);
    setResult(null);
    setActivity('');
    setStatus('starting');
    try {
      const buildResult = await api.build({
        projectType: form.type || undefined,
        target: form.target,
        targetPlatforms: form.targetPlatforms,
        name: form.name,
        description: form.description || undefined,
        github: githubSource ? false : form.github,
        githubRepository: form.github ? form.githubRepository : undefined,
        githubSource,
        deploy: githubSource || ARTIFACT_ONLY_TARGETS.has(form.target) ? false : form.deploy,
        deployProvider: !githubSource && !ARTIFACT_ONLY_TARGETS.has(form.target) && form.deploy ? selectedProvider : undefined,
        domain: form.domain.trim() || undefined,
        envVars: Object.keys(envValues).length > 0 ? envValues : undefined,
        ownershipConsent: ownershipConsent || undefined,
        planApproval: { token: planPreview?.approval?.token || null }
      });
      setStatus('in progress');
      onBuildStarted?.();
      pollTask(buildResult.taskId);
    } catch (err) {
      handleApiError(err);
      setStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!planPreview || !planApproved) {
      await requestPlan();
      return;
    }
    await startBuild();
  };

  // Window sizing (2026-08-22): matches the same fix in AgentChat.jsx's pollBuilderTask -
  // a Cloud Execution Sandbox verification can genuinely take up to the Cloud Run Job's own
  // timeoutSeconds: 900 (backend/execution-worker/cloud-run-job.yaml), and the old 40-attempt/
  // 1500ms window (60s) was sized before that pipeline existed. ~19 minutes of real coverage now.
  const pollTask = (taskId, attemptsLeft = 380) => {
    fetchWithRetry(() => api.task(taskId)).then(res => {
      const task = res.task;
      if (task.currentActivity) setActivity(task.currentActivity);
      if (task.status === 'completed') {
        setStatus(null);
        setActivity('');
        setDecision(null);
        setResult({ taskId, sandboxJobId: task.sandboxJobId, repoUrl: task.repoUrl, pullRequestUrl: task.pullRequestUrl, deployUrl: task.deployUrl, deployDashboardUrl: task.deployDashboardUrl, previewUrl: task.previewUrl, artifactUrl: task.artifactUrl, githubSource: task.config?.githubSource || null, target: task.config?.target || 'WEB', targetPlatforms: task.config?.targetPlatforms || [] });
        setNeededEnvVars(null);
        setConsentRequired(false);
      } else if (task.status === 'failed' && task.awaitingCustomerDecision && task.customerDecision) {
        // A specific, known requirement is still unresolved after auto-repair - not a generic
        // failure. Show the requirement-specific message and let the customer approve one more
        // targeted AI attempt, instead of the generic "check the Logs tab" banner below.
        setStatus(null);
        setActivity('');
        setError('');
        setDecision({ taskId, ...task.customerDecision });
      } else if (task.status === 'failed') {
        setStatus(null);
        setActivity('');
        setDecision(null);
        if (task.needsReconnect) {
          setResult({ needsReconnect: true, reconnectProvider: task.reconnectProvider });
          setError(task.lastError);
        } else if (task.requiredCredentials && task.requiredCredentials.length > 0) {
          setNeededEnvVars(task.requiredCredentials);
          setConsentRequired(Boolean(task.consentRequired));
          setError(task.consentRequired
            ? 'Confirm ownership below before this deploy can proceed.'
            : 'This app needs its own credentials before it can go live \u2014 fill them in below and rebuild.');
        } else {
          setError(task.lastError || 'Build failed \u2014 check the Logs tab for details.');
        }
      } else if (attemptsLeft > 0) {
        setTimeout(() => pollTask(taskId, attemptsLeft - 1), 3000);
      } else {
        setStatus(null);
      }
    }).catch(() => {
      // A Cloud Run restart is recoverable: retain the build state and continue polling
      // rather than making a verified build appear to have disappeared.
      if (attemptsLeft > 0) {
        setError('החיבור התנתק לרגע — מתחברים מחדש וממשיכים לעקוב אחרי הבנייה.');
        setTimeout(() => pollTask(taskId, attemptsLeft - 1), 6000);
        return;
      }
      setStatus(null);
      setError('לא ניתן היה להתחבר מחדש לבדיקת הבנייה. רענון העמוד ינסה לשחזר את המשימה השמורה.');
    });
  };

  const handleApproveDecision = async () => {
    if (!decision?.taskId) return;
    setResolvingDecision(true);
    try {
      await api.resolveRequirementDecision(decision.taskId, true);
      setDecision(null);
      setError('');
      setStatus('in progress');
      pollTask(decision.taskId);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setResolvingDecision(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploadError('');
    setUploadResult(null);
    setUploadStatus('uploading');
    try {
      const res = await api.uploadProject(uploadFile, { github: form.github, deploy: form.deploy, deployProvider: form.deploy ? selectedProvider : undefined, domain: form.domain.trim() || undefined });
      setUploadStatus('processing');
      onBuildStarted?.();
      pollUploadTask(res.taskId);
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message);
      setUploadStatus(null);
    }
  };

  const pollUploadTask = (taskId, attemptsLeft = 380) => {
    fetchWithRetry(() => api.task(taskId)).then(res => {
      const task = res.task;
      if (task.status === 'completed') {
        setUploadStatus(null);
        setUploadResult({ taskId, sandboxJobId: task.sandboxJobId, repoUrl: task.repoUrl, deployUrl: task.deployUrl, deployDashboardUrl: task.deployDashboardUrl, previewUrl: task.previewUrl, target: task.config?.target || 'WEB', targetPlatforms: task.config?.targetPlatforms || [] });
      } else if (task.status === 'failed') {
        setUploadStatus(null);
        if (task.needsReconnect) {
          setUploadResult({ needsReconnect: true, reconnectProvider: task.reconnectProvider });
        }
        setUploadError(task.lastError || 'Could not process the uploaded project.');
      } else if (attemptsLeft > 0) {
        setTimeout(() => pollUploadTask(taskId, attemptsLeft - 1), 3000);
      } else {
        setUploadStatus(null);
      }
    }).catch(() => {
      if (attemptsLeft > 0) {
        setUploadError('החיבור התנתק לרגע — מתחברים מחדש וממשיכים לעקוב אחרי העיבוד.');
        setTimeout(() => pollUploadTask(taskId, attemptsLeft - 1), 6000);
        return;
      }
      setUploadStatus(null);
      setUploadError('לא ניתן היה להתחבר מחדש לבדיקת העיבוד. רענון העמוד ינסה לשחזר את המשימה השמורה.');
    });
  };

  const handleUploadConfirmDeploy = async () => {
    if (!uploadResult?.taskId) return;
    if (uploadDeploymentApproval?.status !== 'APPROVED') {
      setUploadError('Approve this exact verified build before starting deployment.');
      return;
    }
    setConfirmingDeploy(true);
    setUploadError('');
    try {
      const res = await api.confirmDeploy(uploadResult.taskId, getDeployOptions());
      if (res.task.status === 'failed' || res.task.deploymentStatus === 'failed') {
        setUploadError(res.task.lastError || 'Deployment failed — check the Logs tab for details.');
      } else {
        setUploadResult(r => ({ ...r, repoUrl: res.task.repoUrl, deployUrl: res.task.deployUrl, deployDashboardUrl: res.task.deployDashboardUrl }));
        setUploadDeploymentApproval(res.task.deploymentApproval || { ...uploadDeploymentApproval, status: 'CONSUMED' });
      }
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message);
    } finally {
      setConfirmingDeploy(false);
    }
  };

  const handleConfirmDeploy = async () => {
    if (!result?.taskId) return;
    if (deploymentApproval?.status !== 'APPROVED') {
      setError('Approve this exact verified build before starting deployment.');
      return;
    }
    setConfirmingDeploy(true);
    setError(null);
    try {
      const res = await api.confirmDeploy(result.taskId, getDeployOptions({ continuation: Boolean(result.githubSource) }));
      if (res.task.status === 'failed' || res.task.deploymentStatus === 'failed') {
        setError(res.task.lastError || 'Deployment failed — check the Logs tab for details.');
      } else {
        setResult(r => ({ ...r, repoUrl: res.task.repoUrl, pullRequestUrl: res.task.pullRequestUrl, deployUrl: res.task.deployUrl, deployDashboardUrl: res.task.deployDashboardUrl }));
        setDeploymentApproval(res.task.deploymentApproval || { ...deploymentApproval, status: 'CONSUMED' });
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setConfirmingDeploy(false);
    }
  };

  const approveDeployment = async (taskId, isUpload = false) => {
    if (!taskId) return;
    setApprovingDeployment(true);
    if (isUpload) setUploadError(''); else setError(null);
    try {
      const res = await api.approveDeployment(taskId);
      if (isUpload) setUploadDeploymentApproval(res.approval);
      else setDeploymentApproval(res.approval);
    } catch (err) {
      if (isUpload) setUploadError(err.response?.data?.error || err.message);
      else handleApiError(err);
    } finally {
      setApprovingDeployment(false);
    }
  };

  const revokeDeploymentApproval = async (taskId, isUpload = false) => {
    if (!taskId) return;
    try {
      const res = await api.revokeDeploymentApproval(taskId);
      if (isUpload) setUploadDeploymentApproval(res.approval);
      else setDeploymentApproval(res.approval);
    } catch (err) {
      if (isUpload) setUploadError(err.response?.data?.error || err.message);
      else handleApiError(err);
    }
  };

  return (
    <div className="project-builder builder-center">
      <div className="builder-section">
        <div className="section-heading">{t('builder.uploadTitle')}</div>
        <form onSubmit={handleUpload} className="builder-form builder-upload-card">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="builder-upload-icon"><path d="M12 16V4M12 4l-4 4M12 4l4 4" /><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></svg>
          <div className="form-group">
            <label>{t('builder.zipFile')}</label>
          <input type="file" accept=".zip" onChange={e => setUploadFile(e.target.files[0])} />
          <small style={{ marginTop: '0.4rem', display: 'block' }}>
            {t('builder.uploadDescription')}
          </small>
        </div>
        {uploadError && <div className="auth-error">{uploadError}</div>}
        {uploadStatus && <div className="build-status"><span className="spinner" /> {uploadStatus === 'uploading' ? t('builder.uploading') : t('builder.checkingProject')}</div>}
        {uploadResult && (
          <div className="build-status" style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <span>{t('builder.uploadedReady')}</span>
            {uploadResult.previewUrl && !uploadResult.deployUrl && (
              <>
                <button type="button" className="btn" onClick={() => setUploadPreviewOpen(true)}>{t('builder.openPreview')}</button>
                {uploadPreviewOpen && <PreviewFrame loading={false} previewUrl={uploadResult.previewUrl} target={uploadResult.target || 'WEB'} targetPlatforms={uploadResult.targetPlatforms || []} title={t('builder.uploadedPreviewTitle')} returnLabel={t('builder.returnToBuilder')} onClose={() => setUploadPreviewOpen(false)} onRefreshPreview={uploadResult.sandboxJobId ? () => renewBuildPreview(true) : undefined} />}
                {uploadDeploymentApproval?.status !== 'APPROVED' ? (
                  <button type="button" className="submit-btn" onClick={() => approveDeployment(uploadResult.taskId, true)} disabled={approvingDeployment}>
                    {approvingDeployment ? t('builder.approving') : t('builder.approveExactBuild')}
                  </button>
                ) : (
                  <>
                    <small>{t('builder.approvedUntil', { time: new Date(uploadDeploymentApproval.expiresAt).toLocaleTimeString() })}</small>
                    <button type="button" className="submit-btn" onClick={handleUploadConfirmDeploy} disabled={confirmingDeploy}>
                      {confirmingDeploy ? t('builder.deploying') : t('builder.deployApproved')}
                    </button>
                    <button type="button" className="btn" onClick={() => revokeDeploymentApproval(uploadResult.taskId, true)}>{t('builder.revokeApproval')}</button>
                  </>
                )}
              </>
            )}
            {uploadResult.repoUrl && <a href={uploadResult.repoUrl} target="_blank" rel="noreferrer">{t('builder.viewCode')}</a>}
            {uploadResult.deployUrl && <a href={uploadResult.deployUrl} target="_blank" rel="noreferrer">{t('builder.openLive')}</a>}
            {uploadResult.deployDashboardUrl && <a href={uploadResult.deployDashboardUrl} target="_blank" rel="noreferrer">{t('builder.manageDeployment')}</a>}
          </div>
        )}
        <button type="submit" className="submit-btn" disabled={!uploadFile || uploadStatus !== null}>{t('builder.uploadCheck')}</button>
      </form>

      </div>

      <div className="builder-section">
        <div className="section-heading">{t('builder.newProjectTitle')}</div>

        <form onSubmit={handleSubmit} className="builder-form">
        <div className="form-group">
          <label>{t('builder.projectName')}</label>
          <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="my-app" required />
        </div>

        <div className="form-group">
          <label>{t('builder.target')}</label>
          <div className="project-type-grid" role="group" aria-label={t('builder.target')}>
            {TARGET_OPTIONS.map(option => (
              <div key={option.value} className="type-card-wrap">
                <button type="button" className={`type-card ${form.target === option.value ? 'selected' : ''}`} onClick={() => setForm({ ...form, target: option.value, targetPlatforms: PLATFORM_OPTIONS[option.value] ? form.targetPlatforms.filter(platform => PLATFORM_OPTIONS[option.value].includes(platform)) : [], deploy: ARTIFACT_ONLY_TARGETS.has(option.value) ? false : form.deploy })}>
                  {t(option.labelKey)}
                </button>
                <button type="button" className="type-card-info-btn" aria-label={`${t('builder.whatIsThis')}: ${t(option.labelKey)}`} onClick={() => toggleHint('target', option.value)}>?</button>
                {expandedHint?.group === 'target' && expandedHint.value === option.value && (
                  <div className="type-card-explainer">{t(option.hintKey)}</div>
                )}
              </div>
            ))}
          </div>
          {PLATFORM_OPTIONS[form.target] && (
            <div className="build-plan-inputs" style={{ marginTop: '0.75rem' }}>
              <strong>{t(form.target === 'MOBILE' ? 'builder.mobilePlatforms' : 'builder.desktopPlatforms')}</strong>
              {PLATFORM_OPTIONS[form.target].map(platform => (
                <label className="checkbox-label" key={platform}>
                  <input type="checkbox" checked={form.targetPlatforms.includes(platform)} onChange={event => setForm({
                    ...form,
                    targetPlatforms: event.target.checked
                      ? [...form.targetPlatforms, platform]
                      : form.targetPlatforms.filter(item => item !== platform)
                  })} />
                  <span>{platform === 'ANDROID' ? t('builder.android') : platform === 'IOS' ? t('builder.ios') : platform === 'WINDOWS' ? t('builder.windows') : platform === 'MACOS' ? t('builder.macos') : t('builder.linux')}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>{t('builder.projectType')} ({t('builder.projectTypeHint')})</label>
          <div className="project-type-grid">
            {PROJECT_TYPES.map(projectType => (
              <div key={projectType.value} className="type-card-wrap">
                <button type="button" className={`type-card ${form.type === projectType.value ? 'selected' : ''}`} onClick={() => setForm({ ...form, type: form.type === projectType.value ? '' : projectType.value })}>
                  {t(projectType.labelKey)}
                </button>
                <button type="button" className="type-card-info-btn" aria-label={`${t('builder.whatIsThis')}: ${t(projectType.labelKey)}`} onClick={() => toggleHint('type', projectType.value)}>?</button>
                {expandedHint?.group === 'type' && expandedHint.value === projectType.value && (
                  <div className="type-card-explainer">{t(projectType.hintKey)}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>{t('builder.describe')}{form.type ? ` (${t('builder.describeOptional')})` : ` — ${t('builder.describeRequired')}`}</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder={t('builder.describePlaceholder')}
          />
          <small>{t('builder.starterHint')}</small>
        </div>

        <div className="form-group build-plan-control">
          <div className="build-plan-heading">
            <div>
              <label>{t('builder.planTitle')}</label>
              <small>{t('builder.planHint')}</small>
            </div>
            <button type="button" className="btn" onClick={requestPlan} disabled={planLoading || status !== null}>
              {planLoading ? t('builder.preparing') : planPreview ? t('builder.refreshPlan') : t('builder.reviewPlan')}
            </button>
          </div>
          {planPreview && (
            <div className="build-plan-preview">
              <div className="build-plan-summary">
                <strong>{planPreview.projectName}</strong>
                <span>{planPreview.projectType}</span>
                <span>{t('builder.planReady')}</span>
              </div>
              {planPreview.targetCapability && (
                <div className="build-plan-inputs">
                  <strong>{t('builder.targetStatus')}</strong>
                  <span>{planPreview.targetCapability.delivery === 'VERIFIED_ANDROID_PACKAGE'
                    ? t('builder.androidPackageDelivery')
                    : planPreview.targetCapability.supportLevel === 'SUPPORTED'
                      ? planPreview.targetCapability.target === 'FULL_STACK' ? t('builder.fullStackSupported') : t('builder.webSupported')
                      : planPreview.targetCapability.supportLevel === 'EXPERIMENTAL'
                        ? t('builder.targetExperimental')
                        : planPreview.targetCapability.supportLevel === 'COMPANION_ONLY'
                          ? t('builder.mobileCompanion')
                          : t('builder.targetPlanned')}</span>
                  {planPreview.targetCapability.missingEvidence?.length > 0 && <span>{planPreview.targetCapability.delivery === 'VERIFIED_ANDROID_PACKAGE' ? t('builder.androidPackageLimitations') : t('builder.nativeNotAvailable')}</span>}
                </div>
              )}
              <p>{planPreview.intent}</p>
              <div className="build-plan-columns">
                <div>
                  <strong>{t('builder.deliveryPath')}</strong>
                  {planPreview.steps.map((step, index) => <span key={`${step.stage}-${index}`}>{index + 1}. {step.stage} — {step.detail}</span>)}
                </div>
                <div>
                  <strong>{t('builder.qualityGate')}</strong>
                  {planPreview.qualityGate.map(item => <span key={item}>• {item}</span>)}
                </div>
              </div>
              {planPreview.requiredInputs.length > 0 && (
                <div className="build-plan-inputs"><strong>{t('builder.beforeProduction')}</strong>{planPreview.requiredInputs.map(input => <span key={input.key}>• {input.message}</span>)}</div>
              )}
              <label className="checkbox-label build-plan-approval">
                <input type="checkbox" checked={planApproved} onChange={e => setPlanApproved(e.target.checked)} />
                <span>{t('builder.planConsent')}</span>
              </label>
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.github}
              disabled={!github.connected}
              onChange={e => setForm(current => ({
                ...current,
                github: e.target.checked,
                githubRepository: {
                  ...current.githubRepository,
                  name: current.githubRepository.name || current.name.trim().toLowerCase().replace(/[^a-z0-9_.-]+/g, '-').replace(/^-|-$/g, '')
                }
              }))}
            />
            <span>{t('builder.saveGitHub')}{github.connected && ` (${github.username})`}</span>
          </label>
          {!github.loading && !github.connected && (
            <div style={{ marginTop: '0.5rem' }}>
              <button type="button" className="btn" onClick={handleConnectGithub}>{t('builder.connectGitHub')}</button>
              <small style={{ marginTop: '0.4rem' }}>{t('builder.githubHint')}</small>
            </div>
          )}
          {form.github && github.connected && (
            <div className="github-repository-options">
              <label htmlFor="github-repository-name">{t('builder.newRepository')}</label>
              <input id="github-repository-name" type="text" autoComplete="off" value={form.githubRepository.name} onChange={event => setForm(current => ({ ...current, githubRepository: { ...current.githubRepository, name: event.target.value } }))} placeholder="my-project" />
              <small>GitHub ownership: this creates a new repository under @{github.username} only after the verified build and a separate final approval.</small>
              {repositoryAvailability.checking && <span className="github-availability checking">Checking repository name…</span>}
              {!repositoryAvailability.checking && repositoryAvailability.available === true && <span className="github-availability available">Repository name is available.</span>}
              {!repositoryAvailability.checking && repositoryAvailability.available === false && <span className="github-availability occupied">Repository name is already occupied on your GitHub account.</span>}
              {!repositoryAvailability.checking && repositoryAvailability.available === null && repositoryAvailability.reason && <span className="github-availability invalid">{repositoryAvailability.reason}</span>}
              <fieldset className="github-privacy-choice">
                <legend>{t('builder.repositoryPrivacy')}</legend>
                <label><input type="radio" name="github-privacy" checked={form.githubRepository.private} onChange={() => setForm(current => ({ ...current, githubRepository: { ...current.githubRepository, private: true } }))} /> <span>{t('builder.privateRecommended')}</span></label>
                <label><input type="radio" name="github-privacy" checked={!form.githubRepository.private} onChange={() => setForm(current => ({ ...current, githubRepository: { ...current.githubRepository, private: false } }))} /> <span>{t('builder.publicCode')}</span></label>
              </fieldset>
            </div>
          )}
          {github.connected && (
            <div className="github-existing-repository">
              <div className="github-existing-repository-heading">
                <div><strong>Continue from an existing repository</strong><small>First inspect a repository you own in read-only mode. No branch, file or Pull Request will be changed.</small></div>
                <button type="button" className="btn" onClick={loadExistingRepositories} disabled={existingRepository.loading}>{existingRepository.open ? 'Refresh repositories' : 'Choose existing repository'}</button>
              </div>
              {existingRepository.open && (
                <div className="github-existing-repository-content">
                  {existingRepository.loading && <span className="github-availability checking">Loading repositories…</span>}
                  {!existingRepository.loading && existingRepository.repositories.length === 0 && <span className="github-availability invalid">No repositories owned by @{github.username} were found.</span>}
                  {!existingRepository.loading && existingRepository.repositories.length > 0 && <>
                    <label htmlFor="github-existing-repository">Repository</label>
                    <select id="github-existing-repository" value={existingRepository.fullName} onChange={event => selectExistingRepository(event.target.value)}>
                      <option value="">Choose a repository</option>
                      {existingRepository.repositories.map(repository => <option key={repository.fullName} value={repository.fullName}>{repository.fullName}{repository.private ? ' · Private' : ' · Public'}</option>)}
                    </select>
                    {existingRepository.fullName && !existingRepository.loading && <>
                      <label htmlFor="github-existing-branch">Source branch</label>
                      <select id="github-existing-branch" value={existingRepository.branch} onChange={event => setExistingRepository(current => ({ ...current, branch: event.target.value, inspection: null }))}>
                        {existingRepository.branches.map(branch => <option key={branch.name} value={branch.name}>{branch.name}{branch.protected ? ' · protected' : ''}</option>)}
                      </select>
                      <button type="button" className="btn" onClick={inspectExistingRepository} disabled={existingRepository.inspecting || !existingRepository.branch}>{existingRepository.inspecting ? 'Inspecting safely…' : 'Inspect selected source (read-only)'}</button>
                    </>}
                  </>}
                  {existingRepository.inspection && <div className="github-inspection-result"><strong>Source inspected at {existingRepository.inspection.repository.commitSha.slice(0, 12)}</strong><span>{existingRepository.inspection.manifest.files.length} reviewable files · secret scan {existingRepository.inspection.sourceReview.status}</span><small>Source contents remain isolated and are not sent to the agent at this stage. A later edit plan will bind to this exact commit and require a work branch and Pull Request approval.</small></div>}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.deploy}
                disabled={ARTIFACT_ONLY_TARGETS.has(form.target) || !connectedForProvider(selectedProvider)}
                onChange={e => setForm({ ...form, deploy: e.target.checked })}
              />
            <span>{ARTIFACT_ONLY_TARGETS.has(form.target)
              ? t('builder.managedDeploymentUnavailable')
              : `${t('builder.deploy')}${connectedForProvider(selectedProvider) ? ` (${providers.find(p => p.id === selectedProvider)?.name || selectedProvider})` : ''}`}</span>
          </label>

          {form.deploy && (
            <div style={{ marginTop: '0.75rem' }}>
              <label htmlFor="project-domain" style={{ display: 'block', marginBottom: '0.3rem', fontSize: '12.5px', fontWeight: 600 }}>{t('builder.customDomain')}</label>
              <input id="project-domain" type="text" value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} placeholder="app.yourdomain.com" />
              <small style={{ marginTop: '0.35rem', display: 'block' }}>{t('builder.domainHint')}</small>
            </div>
          )}

          <div className="provider-list">
            {providers.map(p => (
              <div key={p.id} className={`provider-card ${selectedProvider === p.id ? 'selected' : ''} ${!p.implemented ? 'disabled' : ''}`}>
                <div className="provider-card-row">
                  <label className="provider-card-label">
                    <input type="radio" name="deployProvider" checked={selectedProvider === p.id} disabled={!p.implemented}
                      onChange={() => setSelectedProvider(p.id)} />
                    <span className="provider-card-name">{p.name}</span>
                    {!p.implemented && <span className="provider-card-soon">(coming soon)</span>}
                  </label>
                  <button type="button" className="btn provider-card-details-btn" onClick={() => setExpandedProvider(expandedProvider === p.id ? null : p.id)}>
                    {expandedProvider === p.id ? t('builder.hideDetails') : t('builder.details')}
                  </button>
                </div>
                {expandedProvider === p.id && (
                  <div className="provider-card-details">
                    <div><strong>{t('builder.freeTier')}</strong> {p.freeTier}</div>
                    <div><strong>{t('builder.bestFor')}</strong> {p.bestFor}</div>
                    <div><strong>{t('builder.limitations')}</strong> {p.limitations}</div>
                    <div><strong>{t('builder.creditCardRequired')}</strong> {p.requiresCreditCard ? t('common.yes') : t('common.no')}</div>
                  </div>
                )}
                {p.implemented && selectedProvider === p.id && !connectedForProvider(p.id) && !providerLoading(p.id) && (
                  <div className="provider-card-connect">
                    <button type="button" className="btn" onClick={() => handleConnectProvider(p.id)}>Connect {p.name}</button>
                    <small style={{ marginTop: '0.4rem', display: 'block' }}>Connect your own {p.name} account once, then every build with Deploy checked goes live automatically to accounts you own  never a shared account, and nothing here can lock or disable your site later.</small>
                  </div>
                )}
                {p.id === 'firebase-hosting' && selectedProvider === p.id && firebase.connected && !firebaseProjects.loading && firebaseProjects.projects.length > 0 && (
                  <div style={{ marginTop: '0.6rem' }}>
                    <label htmlFor="firebase-project-select" style={{ display: 'block', marginBottom: '0.3rem', fontSize: '12.5px', fontWeight: 600 }}>Firebase project for this deployment</label>
                    <select id="firebase-project-select" value={firebaseProjects.selected || ''} onChange={e => handleFirebaseProjectChange(e.target.value)}>
                      {firebaseProjects.projects.map(project => <option key={project.projectId} value={project.projectId}>{project.displayName || project.projectId} ({project.projectId})</option>)}
                    </select>
                    <small style={{ marginTop: '0.35rem', display: 'block' }}>Only Firebase projects accessible through your connected Google account are shown. Your selected project is used for future Firebase deployments.</small>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {(result?.needsReconnect || uploadResult?.needsReconnect) && (
          <div className="auth-error" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <span>Your {providers.find(p => p.id === (result?.reconnectProvider || uploadResult?.reconnectProvider))?.name || 'deploy provider'} connection has expired or was revoked.</span>
            <button type="button" className="btn" onClick={() => handleConnectProvider(result?.reconnectProvider || uploadResult?.reconnectProvider)}>Reconnect now</button>
          </div>
        )}

        {decision && (
          <div className="auth-error" style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', whiteSpace: 'pre-line' }}>
            {decision.message}
            <button type="button" className="submit-btn" onClick={handleApproveDecision} disabled={resolvingDecision} style={{ alignSelf: 'flex-start' }}>
              {resolvingDecision ? 'Starting the fix…' : 'Yes, make this change.'}
            </button>
          </div>
        )}
        {error && !decision && (
          <div className="auth-error">
            {error}
            {error === 'Connect your own GitHub account before selecting a repository.' && (
              <>
                {' '}
                <button type="button" className="submit-btn" onClick={handleConnectGithub}>Connect GitHub</button>
              </>
            )}
            {needsUpgrade && onNavigateToAccount && (
              <>
                {' '}
                <button type="button" className="submit-btn" onClick={onNavigateToAccount}>Upgrade now</button>
              </>
            )}
          </div>
        )}
        {status && (
          <div className="build-status">
            <ThinkingIndicator label={activity || (status === 'starting' ? t('builder.startingBuild') : t('builder.buildingApp'))} />
            <PreviewFrame loading={true} previewUrl={null} />
          </div>
        )}

        {neededEnvVars && (
          <div className="form-group" style={{ background: 'var(--bg-2)', padding: '0.9rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
              {t('builder.appOwnership')}
            </p>
            {neededEnvVars.map(v => (
              <div key={v.name} style={{ marginBottom: '0.5rem' }}>
                <label>
                  {v.name}
                  {v.providerUrl && <> &middot; <a href={v.providerUrl} target="_blank" rel="noreferrer">Get your {v.provider} key</a></>}
                </label>
                <input
                  type="text"
                  value={envValues[v.name] || ''}
                  onChange={e => setEnvValues({ ...envValues, [v.name]: e.target.value })}
                  placeholder={v.comment || `Your own ${v.name}`}
                />
              </div>
            ))}
            <label className="checkbox-label" style={{ marginTop: '0.6rem' }}>
              <input type="checkbox" checked={ownershipConsent} onChange={e => setOwnershipConsent(e.target.checked)} />
              <span>I confirm this app is mine, and every key above is my own, from my own provider account \u2014 not shared or borrowed from anyone else.</span>
            </label>
          </div>
        )}

        {result && (
          <div className="build-status" style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <span>{t('builder.buildComplete')}</span>
            {result.previewUrl && !result.deployUrl && (
              <>
                <button type="button" className="btn" onClick={() => setResultPreviewOpen(true)}>{t('builder.openPreview')}</button>
                {resultPreviewOpen && <PreviewFrame loading={false} previewUrl={result.previewUrl} target={result.target} targetPlatforms={result.targetPlatforms} title={t('builder.builtPreviewTitle')} returnLabel={t('builder.returnToBuilder')} onClose={() => setResultPreviewOpen(false)} onRefreshPreview={result.sandboxJobId ? () => renewBuildPreview(false) : undefined} />}
                {deploymentApproval?.status !== 'APPROVED' ? (
                  <button type="button" className="submit-btn" onClick={() => approveDeployment(result.taskId)} disabled={approvingDeployment}>
                    {approvingDeployment ? t('builder.approving') : result.githubSource ? t('builder.approveForPr') : t('builder.approveExactBuild')}
                  </button>
                ) : (
                  <>
                    <small>{t('builder.approvedUntil', { time: new Date(deploymentApproval.expiresAt).toLocaleTimeString() })}</small>
                    <button type="button" className="submit-btn" onClick={handleConfirmDeploy} disabled={confirmingDeploy}>
                      {confirmingDeploy ? t('builder.deploying') : (result.githubSource ? t('builder.createPullRequest') : t('builder.deployApproved'))}
                    </button>
                    <button type="button" className="btn" onClick={() => revokeDeploymentApproval(result.taskId)}>{t('builder.revokeApproval')}</button>
                  </>
                )}
              </>
            )}
            {result.repoUrl && <a href={result.repoUrl} target="_blank" rel="noreferrer">{t('builder.viewCode')}</a>}
            {result.pullRequestUrl && <a href={result.pullRequestUrl} target="_blank" rel="noreferrer">{t('builder.createPullRequest')}</a>}
            {result.artifactUrl && <button type="button" className="btn" onClick={downloadVerifiedArtifact} disabled={downloadingArtifact}>{downloadingArtifact ? t('builder.preparingArtifact') : t('builder.downloadArtifact')}</button>}
            {result.deployUrl && <a href={result.deployUrl} target="_blank" rel="noreferrer">{t('builder.openLive')}</a>}
            {result.deployDashboardUrl && <a href={result.deployDashboardUrl} target="_blank" rel="noreferrer">{t('builder.manageDeployment')}</a>}
          </div>
        )}

        <div className="builder-footer">
          <button type="submit" className="submit-btn builder-footer-btn" disabled={status !== null || planLoading || (neededEnvVars && consentRequired && !ownershipConsent)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            {neededEnvVars ? t('builder.rebuildCredentials') : planPreview && planApproved ? t('builder.buildApproved') : t('builder.reviewPlan')}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
}

export default ProjectBuilder;
